/**
 * Defines versioned contracts for internal case follow-ups.
 */
import { z } from "zod";
export const CaseFollowUpStatusCodeSchema = z.enum([
    "PROGRAMMED",
    "COMPLETED",
    "NOT_COMPLETED",
    "CANCELLED",
]);
export const CaseFollowUpRecipientModeSchema = z.enum(["BOTH", "SELF_ONLY"]);
export const CaseFollowUpHistoryChangeTypeSchema = z.enum([
    "CREATED",
    "RESCHEDULED",
    "COMPLETED",
    "NOT_COMPLETED",
    "CANCELLED",
    "NOT_YET",
]);
export const CaseFollowUpMilestoneSchema = z.enum([
    "CREATED",
    "REMINDER_3D",
    "REMINDER_1D",
    "DUE",
    "OVERDUE_1H",
    "DAILY_OVERDUE",
    "RESCHEDULED",
]);
export const CaseFollowUpEventName = "cases.follow-up-milestone.v1";
export const CaseFollowUpEventPayloadSchema = z
    .object({
    actorDisplayName: z.string().min(1).max(200).nullable(),
    branchId: z.uuid().nullable(),
    caseId: z.uuid(),
    companyId: z.uuid(),
    followUpId: z.uuid(),
    milestone: CaseFollowUpMilestoneSchema,
    occurredAt: z.iso.datetime({ offset: true }),
    publicId: z.string().regex(/^\d+$/),
    scheduleGeneration: z.number().int().positive(),
    scheduledAt: z.iso.datetime({ offset: true }),
})
    .strict();
export const CaseFollowUpActorSchema = z
    .object({
    displayName: z.string().min(1).max(200),
    userId: z.uuid(),
})
    .strict();
export const CaseFollowUpStatusSchema = z
    .object({
    title: z.string().min(1).max(120),
    value: CaseFollowUpStatusCodeSchema,
})
    .strict();
export const CaseFollowUpHistoryItemSchema = z
    .object({
    actorDisplayName: z.string().min(1).max(200),
    changeType: CaseFollowUpHistoryChangeTypeSchema,
    changedAt: z.iso.datetime({ offset: true }),
    newScheduledAt: z.iso.datetime({ offset: true }).nullable(),
    previousScheduledAt: z.iso.datetime({ offset: true }).nullable(),
    reason: z.string().nullable(),
    recipientMode: CaseFollowUpRecipientModeSchema.nullable(),
})
    .strict();
export const CaseFollowUpItemSchema = z
    .object({
    canManage: z.boolean(),
    createdAt: z.iso.datetime({ offset: true }),
    createdBy: CaseFollowUpActorSchema,
    description: z.string().nullable(),
    history: z.array(CaseFollowUpHistoryItemSchema),
    id: z.uuid(),
    reason: z.string().nullable(),
    recipients: z.array(CaseFollowUpActorSchema),
    scheduledAt: z.iso.datetime({ offset: true }),
    status: CaseFollowUpStatusSchema,
})
    .strict();
export const CaseFollowUpListQuerySchema = z
    .object({
    scope: z.enum(["mine", "all"]).default("mine"),
})
    .strict();
export const CaseFollowUpListResponseSchema = z
    .object({
    canViewAll: z.boolean(),
    items: z.array(CaseFollowUpItemSchema),
    scope: z.enum(["mine", "all"]),
})
    .strict();
/**
 * Sort keys for the company-wide follow-up work queue.
 */
export const WorkspaceFollowUpSortSchema = z.enum(["SCHEDULED_ASC", "SCHEDULED_DESC"]);
/**
 * Compact follow-up row for the workspace queue (no history payload).
 */
export const WorkspaceFollowUpListItemSchema = z
    .object({
    bookNumber: z.string().nullable(),
    branchName: z.string().nullable(),
    casePublicId: z.string().regex(/^\d+$/),
    caseStatus: z
        .object({
        title: z.string().min(1).max(120),
        value: z.string().min(1).max(64),
    })
        .strict(),
    createdAt: z.iso.datetime({ offset: true }),
    createdBy: CaseFollowUpActorSchema,
    description: z.string().nullable(),
    id: z.uuid(),
    reason: z.string().nullable(),
    scheduledAt: z.iso.datetime({ offset: true }),
    status: CaseFollowUpStatusSchema,
})
    .strict();
/**
 * Filters for the company-wide follow-up work queue.
 */
export const WorkspaceFollowUpListQuerySchema = z
    .object({
    casePublicId: z.string().regex(/^\d+$/).optional(),
    from: z.iso.date().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(50).default(20),
    scope: z.enum(["mine", "all"]).default("mine"),
    sort: WorkspaceFollowUpSortSchema.default("SCHEDULED_ASC"),
    status: CaseFollowUpStatusCodeSchema.optional(),
    to: z.iso.date().optional(),
})
    .strict()
    .refine((value) => value.from === undefined || value.to === undefined || value.from <= value.to, "The start date must not be after the end date.");
export const WorkspaceFollowUpListResponseSchema = z
    .object({
    canViewAll: z.boolean(),
    items: z.array(WorkspaceFollowUpListItemSchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    scope: z.enum(["mine", "all"]),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
})
    .strict();
export const CreateCaseFollowUpRequestSchema = z
    .object({
    description: z.string().trim().min(1).max(4_000).optional(),
    reason: z.string().trim().min(1).max(240).optional(),
    scheduledAt: z.iso.datetime({ offset: true }),
})
    .strict();
export const RescheduleCaseFollowUpRequestSchema = z
    .object({
    description: z.string().trim().min(1).max(4_000).nullable().optional(),
    reason: z.string().trim().min(1).max(240).nullable().optional(),
    recipientMode: CaseFollowUpRecipientModeSchema.optional(),
    scheduledAt: z.iso.datetime({ offset: true }),
})
    .strict();
export const CancelCaseFollowUpRequestSchema = z
    .object({
    cancellationReason: z.string().trim().min(1).max(1_000).optional(),
})
    .strict();
export const CaseFollowUpParametersSchema = z
    .object({
    followUpId: z.uuid(),
    publicId: z.string().regex(/^\d+$/),
})
    .strict();
export const CasePublicIdParametersSchema = z
    .object({
    publicId: z.string().regex(/^\d+$/),
})
    .strict();
//# sourceMappingURL=FollowUpContract.js.map